import { useQueryClient } from "@tanstack/react-query";
import { Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IPatientClinicianForm } from "../../../../@types/Proms";
import { usePatientInfo } from "../../../../hooks/queries/patients";
import { useToast } from "../../../../hooks/useToast";
import { surveysService } from "../../../../services/surveysService";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  Divider,
  FormOption,
  FormsContainer,
  LoadingContainer,
  ModalContainer,
  spinCss,
} from "./AddFormModal.styles";
import { AnswerClinicianFormModal } from "./AnswerClinicianFormModal";

type AddFormModalProps = {
  patientId: string;
  clinicianForms: IPatientClinicianForm[];
  onClose: () => void;
};

export function AddFormModal({
  patientId,
  clinicianForms,
  onClose,
}: AddFormModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { patient } = usePatientInfo({ patientId });
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [userSurveyId, setUserSurveyId] = useState<string>();

  const handleFormClick = async (surveyId: string) => {
    setIsLoading(true);

    const request = await surveysService.getUserSurveyIdBySurveyId(
      surveyId,
      patientId
    );

    if (request.status < 200 || request.status >= 300) {
      showToast(t("patientPage_forms_fetchError"), "error", {
        rightMargin: "250px",
      });
      setIsLoading(false);

      return;
    }

    setUserSurveyId(request.data);
    setIsLoading(false);

    mixpanelActions.track("Visited Screen: Form screen");
  };

  const handleFormsContainer = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <Spin className={spinCss()} size="large" />
        </LoadingContainer>
      );
    }

    return clinicianForms.map((form, index) => {
      return (
        <>
          <FormOption onClick={() => handleFormClick(form.id)}>
            {form.name}
          </FormOption>

          {index !== clinicianForms.length - 1 ? <Divider /> : null}
        </>
      );
    });
  };

  const handleFormSubmit = (responseStatus?: number) => {
    setUserSurveyId(undefined);

    if (responseStatus)
      if (responseStatus >= 200 && responseStatus < 300) {
        showToast(t("patientPage_forms_added"), "success", {
          rightMargin: "50px",
        });

        queryClient.refetchQueries({
          queryKey: ["patient-proms-infinite", Number(patientId)],
          exact: false,
        });

        onClose();
      } else {
        showToast(t("patientPage_forms_submitError"), "error", {
          rightMargin: "250px",
        });
      }
  };

  const handleFormCancel = () => {
    setUserSurveyId(undefined);
  };

  useEffect(() => {
    if (clinicianForms.length === 1) {
      handleFormClick(clinicianForms[0].id);
    }
  }, []);

  return (
    <ModalContainer>
      {userSurveyId === undefined ? (
        <FormsContainer>{handleFormsContainer()}</FormsContainer>
      ) : (
        <AnswerClinicianFormModal
          patient={patient}
          userSurveyId={userSurveyId}
          handleFormCancel={handleFormCancel}
          handleFormSubmit={handleFormSubmit}
        />
      )}
    </ModalContainer>
  );
}
